import {CloseOutlined, LeftOutlined} from "@ant-design/icons";
import {Button, Form, Spin, Tabs} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiFactory from "../../../api";
import { AuthorityConfiguration } from "../../../components/project/AuthorityConfiguration";
import { ProjectInformation } from "../../../components/project/ProjectInformation";
import { TaskPropertyItem } from "../../../components/project/TaskPropertyItem";
import { WorkFlow } from "../../../components/project/WorkFlow";
import {
  AUTHORITY_CONFIG,
  typeConfirms,
  USAGE_STATUS,
} from "../../../config/Constant";
import { useWorkManagementContext } from "../../../context/WorkManagementContext";
import { useSideBarStore } from "../../../store/SideBarStore";
import { useInfoUser } from "../../../store/UserStore";
import {getUserPermissions, openNotificationError, openNotificationSuccess} from "../../../utils/Utils";
import "./style.scss";
import { GeneralModal } from "../../../components/modal/GeneralModal";
import MobileProjectTabs from "../../../components/project/MobileProjectTabs";

const ProjectDetail = ({ different }) => {
  const navigate = useNavigate();
  const { user, languageMap } = useInfoUser();
  const { projectCode } = useParams();
  const [form] = Form.useForm();
  const {
    projectTypeOptions,
    progressStepOptions,
    roleOptions,
    authorityOptions,
    propertyOptions,
    stageWorkFlowOption,
    attributeTemplate,
  } = useWorkManagementContext();
  const { switchIsWorkManagementOptions, isWorkManagementOptions } =
    useSideBarStore((state) => state);
  const [initialValues, setInitialValues] = useState(null);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [projectManager, setProjectManager] = useState(null);
  const [permissionCurrentUser, setPermissionCurrentUser] = useState(null);

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [typeConfirm, setTypeConfirm] = useState("");
  const [titleConfirmModal, setTitleConfirmModal] = useState("");
  const [propertyItems, setPropertyItems] = useState([]);

  const filteredOrganizations = userOrganizations.filter(
    (org) => org.objectId === projectManager?.objectId
  );
  const [isVerify, setIsVerify] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState("projectInformation");

  const renderPaths = (paths) => {
    return paths.map((path, index) => (
      <span key={path.objectId}>
        {path.name}
        {index < paths.length - 1 ? " -> " : ""}
      </span>
    ));
  };

  const renderOrganizations = () => {
    if (filteredOrganizations.length > 0) {
      return filteredOrganizations.map((user) => (
        <div key={user.objectId}>
          {user.paths.length > 0 ? (
            <span className="text-[#B8B8B8] text-[12px]">
              {renderPaths(user.paths)}
            </span>
          ) : (
            <span className="text-[#B8B8B8] text-[12px]">
              No paths available
            </span>
          )}
        </div>
      ));
    } else {
      return (
        <div className="text-center text-[#B8B8B8] text-[14px]">
          No organizations available
        </div>
      );
    }
  };

  const openModalUpdate = (type) => {
    form
      .validateFields()
      .then(() => {
        if (type) {
          setOpenModalConfirm(true);
          setTypeConfirm(type);
        }
      })
      .catch(() => {
        setOpenModalConfirm(false);
      });
  };

  const openModal = (type) => {
    if (type) {
      setOpenModalConfirm(true);
      setTypeConfirm(type);
    }
  };

  const closeModal = () => {
    setOpenModalConfirm(false);
    setTypeConfirm("");
  };

  const handleConfirm = async () => {
    if (typeConfirm === typeConfirms.DELETE_PROJECT) {
      await deleteProject();
    } else if (typeConfirm === typeConfirms.UPDATE_PROJECT) {
      form.submit();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };
  const messageTitleModal = () => {
    if (typeConfirm === typeConfirms.DELETE_PROJECT) {
      setTitleConfirmModal(
        languageMap?.["project.management.modal.detail.deleteProject"] ??
          "Do you want to delete this project?"
      );
      return;
    } else if (typeConfirm === typeConfirms.UPDATE_PROJECT) {
      setTitleConfirmModal(
        languageMap?.["project.management.modal.detail.updateProject"] ??
          "Do you want to update this project?"
      );
      return;
    } else {
      setTitleConfirmModal("");
      return;
    }
  };

  useEffect(() => {
    if (isVerify) {
      messageTitleModal();
    }
  }, [typeConfirm, isVerify]);

  const onFinish = async (value) => {
    const {
      participantRequest,
      projectAttributeRequest,
      progressRequests,
      actualStartDate,
      actualEndDate,
      startDate,
      endDate,
      autoNotification,
    } = value;

    const propertyItemsMap = propertyItems.reduce((acc, item) => {
      acc[item.code] = item?.isActive;
      return acc;
    }, {});

    if (!participantRequest) {
      return openNotificationError("Participant not empty!");
    }

    if (!progressRequests) {
      return openNotificationError("Work flow not empty!");
    }

    const participantFromManager = {
      authorityCode: authorityOptions[0].code,
      participantCode: projectManager?.objectId,
      roleCode: roleOptions[0].code,
    };

    participantRequest.push(participantFromManager);

    const processedValue = {
      ...value,
      managerCode: projectManager?.objectId,
      actualStartDate: actualStartDate ? actualStartDate?.toISOString() : null,
      actualEndDate: actualEndDate ? actualEndDate?.toISOString() : null,
      startDate: startDate ? startDate?.toISOString() : null,
      endDate: endDate ? endDate?.toISOString() : null,
      autoNotification: autoNotification ? USAGE_STATUS.YES : USAGE_STATUS.NO,
      projectAttributeRequest: projectAttributeRequest
        ?.map((proAttrReq) => {
          return {
            ...proAttrReq,
            isActive: propertyItemsMap?.[proAttrReq?.attributeCode],
          };
        })
        ?.filter(Boolean),
      progressRequests: progressRequests
        .filter(Boolean)
        .sort((a, b) => a.sortSequence - b.sortSequence),
      participantRequest: participantRequest.filter(Boolean),
    };

    if (different.type === "CREATED") {
      try {
        const response =
          await apiFactory.projectApi.createProject(processedValue);
        if (response?.status === 200) {
          navigate("/work-management/dashboard");
          openNotificationSuccess("Create project success!");
        }
      } catch (error) {
        openNotificationError("Failed to create project.");
      }
    }
    if (different.type === "UPDATED") {
      try {
        processedValue.projectCode = initialValues.projectCode;

        const response =
          await apiFactory.projectApi.updateProject(processedValue);

        if (response?.status === 200) {
          navigate("/work-management/dashboard");
          openNotificationSuccess("Update project success!");
        } else {
          return openNotificationError(response?.message);
        }
      } catch (error) {
        openNotificationError("Failed to update project.");
      }
    }
  };

  const deleteProject = async () => {
    try {
      const response = await apiFactory.projectApi.deleteProject(projectCode);
      if (response?.status === 200) {
        navigate("/work-management/dashboard");
        openNotificationSuccess("Delete project success!");
      } else {
        return openNotificationError(response?.message);
      }
    } catch (error) {
      openNotificationError("Failed to delete project.");
    }
  };

  const fetchUserOrganizations = async () => {
    try {
      const request = {
        name: "",
        module: "WORK_MANAGEMENT",
      };

      const response =
        await apiFactory.organizationFromWorkManageApi.search(request);

      if (response?.status !== 200) {
        setIsLoadingPage(false);
        return;
      }

      setUserOrganizations(response?.data);
    } catch (error) {
    } finally {
      setIsLoadingPage(false);
    }
  };

  const createPathString = (userOrganization) => {
    if (userOrganization?.paths?.length > 0) {
      return (
        userOrganization?.paths.map((path) => path?.name).join(" -> ") +
        " -> " +
        userOrganization?.name
      );
    } else {
      return "UNKNOWN GROUP";
    }
  };

  const initialValuesCreate = () => {
    const userOrganization = userOrganizations
      .filter((userOrganization) => userOrganization.type === "PERSON")
      .find((item) => item.objectId === user.userId);
    //const pathString = createPathString(userOrganization);

    setInitialValues({
      projectId: null,
      name: null,
      managerCode: null,
      segmentCode: null,
      progressStepCode: null,
      startDate: null,
      endDate: null,
      actualStartDate: null,
      actualEndDate: null,
      remark: null,
      color: null,
      autoNotification: null,
      participantRequest: [],
      projectAuthorityRequest: [],
      projectAttributeRequest: [],
      progressRequests: [],
    });
    if (userOrganization) {
      userOrganization.userCode = userOrganization.objectId;
    }

    setProjectManager(userOrganization);
  };

  const initialValuesUpdate = async () => {
    const response = await apiFactory.projectApi.getProjectDetail(projectCode);

    if (!response) return;

    const { status, message, data } = response;

    if (status === 403) {
      // navigate("/work-management/dashboard");
      navigate(-1);
      return openNotificationError(message);
    }

    if (status === 200) {
      const userList = userOrganizations.filter(
        (userOrganization) => userOrganization.type === "PERSON"
      );
      const userOrganization = userList.find(
        (item) => item.objectId === data.managerCode
      );
      if (userOrganization) {
        userOrganization.userCode = userOrganization.objectId;
      }
      // const userCode = userList.find(
      //   (item) => item.objectId === user.userId
      // ).userCode;
      const managerCode = user?.userId;
      const pathString = createPathString(userOrganization);
      const permission = getUserPermissions(
        projectCode,
        AUTHORITY_CONFIG.PROJECT_MANAGEMENT.ID,
        user?.userId,
        data.projectAuthorityDtos,
        data.projectParticipantDtos
      );

      const projectAuthorityRequest = Object.values(AUTHORITY_CONFIG).map(
        (configItem) => {
          const projectAuthority = data.projectAuthorityDtos.find(
            (item) => item.authorityId === configItem.ID
          );
          return {
            ...projectAuthority,
            label: configItem.LABEL,
          };
        }
      );

      const updateInitialValues = {
        type: different.type,
        projectCode: data.projectCode,
        projectId: data.projectId,
        name: data.name,
        managerCode: pathString,
        segmentCode: data.segmentCode,
        progressStepCode: data.progressStepCode,
        startDate: data.startDate ? dayjs(data.startDate) : null,
        endDate: data.endDate ? dayjs(data.endDate) : null,
        actualStartDate: data.actualStartDate
          ? dayjs(data.actualStartDate)
          : null,
        actualEndDate: data.actualEndDate ? dayjs(data.actualEndDate) : null,
        remark: data.remark || null,
        color: data.color,
        autoNotification: data.autoNotification,
        participantRequest: data.projectParticipantDtos.filter(
          (item) => item.userCode !== managerCode
        ),
        projectAuthorityRequest: projectAuthorityRequest,
        projectAttributeRequest: data.projectAttributeDtos,
        progressRequests: data.projectProgressDtos.sort(
          (a, b) => a.sortSequence - b.sortSequence
        ),
      };

      setInitialValues(updateInitialValues);
      setProjectManager(userOrganization);
      setPermissionCurrentUser(permission);
    }
  };

  const checkOrgUser = async () => {
    setIsLoadingPage(true);
    try {
      const result = await apiFactory.organizationUserApi.existOrgUser();

      if (result?.status !== 200) {
        setIsLoadingPage(false);
        return;
      }

      setIsVerify(result?.data);
    } catch (error) {}
  };

  const handleNextTab = () => {
    switch(activeTab) {
      case "projectInformation":
        setActiveTab("taskProperty");
        break;
      case "taskProperty":
        setActiveTab("workflow");
        break;
      case "workflow":
        setActiveTab("authority");
        break;
      default:
        break;
    }
  }

  const handlePreviousTab = () => {
    switch(activeTab) {
      case "taskProperty":
        setActiveTab("projectInformation");
        break;
      case "workflow":
        setActiveTab("taskProperty");
        break;
      case "authority":
        setActiveTab("workflow");
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (isVerify) {
      fetchUserOrganizations();
    }
  }, [isVerify]);

  useEffect(() => {
    if (isVerify) {
      const initialize = () => {
        if (different?.type === "CREATED") {
          initialValuesCreate();
        }

        if (different?.type === "UPDATED") {
          initialValuesUpdate();
        }
      };

      if (userOrganizations.length > 0 && user) {
        initialize();
      }
    }
  }, [user, userOrganizations, different, projectCode, isVerify]);

  useEffect(() => {
    checkOrgUser();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const authorityConfiguration = () => {
    return (
        <>
          <div className="font-bold py-2">
            {languageMap?.["project.management.config.title"] ??
            "Project Authority Configuration"}
          </div>
          <AuthorityConfiguration
              initialValues={initialValues}
              authorityOptions={authorityOptions}
              projectManager={projectManager}
              isMobile={isMobile}
          />
        </>
    );
  };

  const projectInformation = () => {
    return (
        <ProjectInformation
              initialValues={initialValues}
              form={form}
              projectTypeOptions={projectTypeOptions}
              progressStepOptions={progressStepOptions}
              roleOptions={roleOptions}
              authorityOptions={authorityOptions}
              projectManager={projectManager}
              permissionCurrentUser={permissionCurrentUser}
              different={different}
              renderOrganizations={renderOrganizations}
              isMobile={isMobile}
        />
    );
  };

  const taskPropertyItem = () => {
    return (
        <TaskPropertyItem
            initialValues={initialValues}
            attributeTemplate={attributeTemplate}
            propertyOptions={propertyOptions}
            permissionCurrentUser={permissionCurrentUser}
            different={different}
            propertyItems={propertyItems}
            setPropertyItems={setPropertyItems}
        />
    );
  };

  const workFlow = () => {
    return (
        <WorkFlow
            isMobile = {isMobile}
            initialValues={initialValues}
            stageWorkFlowOption={stageWorkFlowOption}
            form={form}
            permissionCurrentUser={permissionCurrentUser}
            different={different}
        />
    );
  };

  const footerButton = () => {
    return (
      /* Footer Buttons */
      <div className="flex justify-end align-end project-footer mb-2">
        <Button
            type="primary"
            htmlType="submit"
            hidden={different.type === "UPDATED"}
            className="mr-2"
        >
          {languageMap?.["project.management.config.createBtn"] ??
          "Create"}
        </Button>

        <Button
            type="primary"
            hidden={different.type === "CREATED"}
            disabled={
              !permissionCurrentUser?.includes("U") || !projectManager
            }
            className="mr-2"
            onClick={() => openModalUpdate(typeConfirms.UPDATE_PROJECT)}
        >
          {languageMap?.["project.management.config.updateBtn"] ??
          "Update"}
        </Button>

        <Button
            type="primary"
            danger
            hidden={different.type === "CREATED"}
            disabled={
              !permissionCurrentUser?.includes("D") || !projectManager
            }
            onClick={() => openModal(typeConfirms.DELETE_PROJECT)}
        >
          {languageMap?.["project.management.config.deleteBtn"] ??
          "Delete"}
        </Button>
      </div>
  );
  };

  return (
    <>
      {!isLoadingPage ? (
        initialValues && (
          <Form
            form={form}
            className="pl-2 pr-2"
            onFinish={onFinish}
            initialValues={initialValues}
            onValuesChange={(value) => {
              const formValues = form.getFieldsValue();

              if (value.segmentCode) {
                const selectedType = projectTypeOptions.find(
                  (item) => item.code === value.segmentCode
                );
                const formData = {
                  ...formValues,
                  color: selectedType?.color,
                };
                form.setFieldsValue(formData);
              }
            }}
            onKeyDown={handleKeyDown}
          >
            {/* Header */}
            <div className="pt-2">
              <div className={`${isMobile ? 'fixed-header-detail' : ''}`}>
                <div className="title ">
                  {!isWorkManagementOptions && (
                    <a
                      className="btn-back-to-work-management"
                      onClick={switchIsWorkManagementOptions}
                    >
                      <LeftOutlined size={25} />
                    </a>
                  )}
                  <span>
                    {languageMap?.["project.management.title"] ??
                      "Project Management"}
                  </span>
                </div>

                  {isMobile && (
                      <MobileProjectTabs
                          activeTab={activeTab}
                          onChange={setActiveTab}
                          languageMap={languageMap}
                      />
                  )}
              </div>

              {/* Main Content */}
              <div className="project-management"
                   style={{ marginTop: isMobile ? "80px" : "" }}>

                {!isMobile ? (
                    <div className="project-information border-right p-2">
                      {projectInformation()}
                    </div>
                ) :
                    <div className={`project-information border-right p-2 ${activeTab !== "projectInformation" ? "hidden" : ""}`}>
                      {projectInformation()}
                    </div>
                }


                <div className={`project-task p-2 ${isMobile && activeTab === "authority" ? "hidden" : ""}`}>
                  <div className={`${!isMobile || activeTab === "taskProperty" ? "" : "hidden"}`}>
                    {taskPropertyItem()}
                  </div>
                  <div className={`${!isMobile || activeTab === "workflow" ? "" : "hidden"}`}>
                    {workFlow()}
                  </div>
                </div>

              </div>
              {!isMobile && <hr />}
            </div>

            {/* Authority & Footer (Mobile/Desktop) */}
            <div className="project-management">
              {/* Authority Configuration - use className to hide/unhide */}
              <div className="project-authority p-2" >
                <div className={`${!isMobile || activeTab === "authority" ? "" : "hidden"}`}>
                  {authorityConfiguration()}
                </div>
              </div>

              {/* Footer buttons */}
              {isMobile ? (
                  <div className={`fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-md flex justify-between items-center 
                   ${activeTab === "authority" ? "authority-footer" : ""}`}>
                    {/* Previous Button */}
                    <div className={`${activeTab !== "projectInformation" ? "" : "invisible"}`}>
                      <Button
                          type="default"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePreviousTab();
                          }}
                          className="ml-4"
                      >
                        {languageMap?.["project.details.buttonPreviousStep"] ?? "Previous"}
                      </Button>
                    </div>

                    <div className="flex items-center ml-auto">
                      {/* Next/Submit Buttons */}
                      {activeTab === "authority" ? (
                          <>
                            <Button
                                type="primary"
                                htmlType="submit"
                                hidden={different.type === "UPDATED"}
                                className="mr-2"
                            >
                              {languageMap?.["project.management.config.createBtn"] ?? "Create"}
                            </Button>
                            <Button
                                type="primary"
                                hidden={different.type === "CREATED"}
                                disabled={!permissionCurrentUser?.includes("U") || !projectManager}
                                className="mr-2"
                                onClick={() => openModalUpdate(typeConfirms.UPDATE_PROJECT)}
                            >
                              {languageMap?.["project.management.config.updateBtn"] ?? "Update"}
                            </Button>
                            <Button
                                type="primary"
                                danger
                                hidden={different.type === "CREATED"}
                                disabled={!permissionCurrentUser?.includes("D") || !projectManager}
                                onClick={() => openModal(typeConfirms.DELETE_PROJECT)}
                            >
                              {languageMap?.["project.management.config.deleteBtn"] ?? "Delete"}
                            </Button>
                          </>
                      ) : (
                          <Button
                              type="primary"
                              onClick={(e) => {
                                e.preventDefault();
                                handleNextTab();
                              }}
                          >
                            {languageMap?.["project.details.buttonNextStep"] ?? "Next"}
                          </Button>
                      )}
                    </div>
                  </div>
              ) : (
                  footerButton()
              )}
            </div>

          </Form>
        )
      ) : (
        <div className="w-full h-full flex justify-center items-center">
          <Spin />
        </div>
      )}

      {/* Confirmation Modal */}
      {openModalConfirm && (
        <GeneralModal
          open={openModalConfirm}
          onCancel={closeModal}
          title={titleConfirmModal}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export { ProjectDetail };
